import React, { useCallback, useState } from "react";
import { LiaTimesSolid } from "react-icons/lia";
import { useDispatch, useSelector } from "react-redux";
import {
  ActionTitle,
  DisplayBetween,
  ThemeItem,
} from "../../common-components/StyledComponents.jsx";
import ScrollLoader from "../../common-components/ScrollLoader.jsx";
import { PageLoader } from "../../common-components/Loaders.js";
import ConfirmationDialog from "../../components/popups/ConfirmationDialog.jsx";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll.js";
import { apiGet, apiPost } from "../../library/utils/common-services/apiCall.js";
import { ENDPOINTS } from "../../library/utils/constants/apiurl.js";
import { EDITOR_TYPES } from "../../library/utils/constants/index.js";
import {
  getCanvasSize,
  getActiveEditorType,
} from "../../library/utils/helpers/index.js";
import {
  applyTheme,
  changeObjectsInAllPages,
  deSelectSafeArea,
  setCalendarSettings,
  setCurrentObjectProperties,
  setPageNumber,
  setSettings,
} from "../../store/slices/canvas.js";
import { store, setSuppressLayoutHistory } from "../../store/store.jsx";
import { getCartOrderId } from "../../store/slices/projectSetup.js";
import { setIsActionActive } from "../../store/slices/appAlice.js";
import { processProjectPages } from "../../library/utils/services/theme/index.js";
import { getMasksByIds } from "../../library/utils/services/mask/index.js";
import {
  getMaskIds,
  replaceMaskIdWithPath,
} from "../../library/utils/common-functions/index.js";
import BlankImagePlaceholder from "../../assets/images/blankImagePlaceholder.png";

const PROJECTS_PER_PAGE = 20;

// Only settings that change page GEOMETRY are carried over from the loaded design.
// A merge (setSettings), never replaceSettings — the saved blob also contains
// `selectedMenuItems`, which drives the customer's whole sidebar (Sidebar.jsx),
// and an old project's copy would silently rewrite the current tab list.
// `shape` is deliberately NOT here: it needs a paired setCanvasSize dispatch to
// take effect (see useThemeSetup), and Header re-derives it from canvasSize on save.
const GEOMETRY_SETTING_KEYS = [
  "subtype",
  "coverEnabled",
  "showFullCoverSheet",
  "hideLastCover",
  "spineWidth",
  "paperThickness",
  "isFoldable",
];

// Order titles are slugs with a trailing timestamp ("romantic-beach-pre-wedding_1769778005979").
const prettyTitle = (title) =>
  (title || "").replace(/_\d{6,}$/, "").replace(/-/g, " ").trim() || "Saved design";

// A customer can have many near-identical saves of the same design — same title,
// same preview image, same page count. The save date is the only field that
// actually tells them apart, so it has to be on the tile.
const savedOn = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

// Fixed-height thumbnail box: ThemeItem is height:100%, so without a sized parent
// every tile resolved to a different height and the captions collided with the row
// below. `contain` because a photobook spread is ~3:1 and `cover` crops it to nothing.
const THUMB_BOX_HEIGHT = 92;

// Collect the photos the customer has ALREADY placed in the current project, in the
// shape `changeObjectsInAllPages` expects. Mirrors useThemeSetup.replacePages so a
// project load behaves like a theme switch. Two deliberate differences from that
// copy, both of which only ever ADD images (they cannot lose any):
//   • safeAreaObjects are scanned too — the theme path's collector skips them even
//     though its clear step empties them, which silently drops those photos.
//   • objects with no `large` url are skipped (nothing placeable to carry over).
const collectPlacedImages = (pages) => {
  const collected = [];
  if (!Array.isArray(pages)) return collected;
  const take = (obj) => {
    if (obj?.type !== "img" || !obj.url || obj.url.trim() === "") return;
    const largeUrl = obj.urls?.find((u) => u.size === "large");
    if (!largeUrl?.url) return;
    const w = largeUrl?.w || obj.image?.originalWidth || obj.image?.width || 0;
    const h = largeUrl?.h || obj.image?.originalHeight || obj.image?.height || 0;
    collected.push({
      urls: obj.urls.map((u) =>
        u.size === "large" ? { ...u, w: w || u.w, h: h || u.h } : u,
      ),
      image_id: obj.image_id,
      originalWidth: w,
      originalHeight: h,
    });
  };
  pages.forEach((page) =>
    (page.layout || []).forEach((layout) => {
      if (!layout) return;
      (layout.objects || []).forEach(take);
      (layout.safeAreaObjects || []).forEach(take);
    }),
  );
  return collected;
};

// Empty every customer photo slot in the loaded design, so its own photos are NOT
// carried over. `displaySameImage` ("Keep Image Fixed") is design content the
// template author locked, not a customer slot — every other path in the app
// excludes it, so it keeps its image here too.
// image_id / pendingImageId are cleared as well (the theme path leaves them): the
// ids belong to the OTHER project's image library, a stale image_id inflates the
// Photos-tab usage count for a photo that isn't placed, and a dangling
// pendingImageId blocks Save with no way to retry.
const clearPlacedImages = (pages) => {
  const empty = (obj) => {
    if (obj?.type !== "img" || obj.displaySameImage) return obj;
    const next = { ...obj, url: "", urls: [] };
    delete next.image_id;
    delete next.pendingImageId;
    return next;
  };
  return pages.map((page) => ({
    ...page,
    layout: (page.layout || []).map((layout) =>
      layout
        ? {
          ...layout,
          objects: (layout.objects || []).map(empty),
          safeAreaObjects: layout.safeAreaObjects?.map(empty),
        }
        : layout,
    ),
  }));
};

const ProjectGridItem = React.memo(({ project, onClick }) => {
  const name = prettyTitle(project.title);
  const date = savedOn(project.updatedAt);
  const meta = [date, project.number_of_pages && `${project.number_of_pages} pages`, project.size]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="col-6 mb-3">
      <div
        style={{
          height: THUMB_BOX_HEIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <ThemeItem
          src={project.preview || BlankImagePlaceholder}
          alt={name}
          title={`${name}${date ? ` — saved ${date}` : ""}`}
          className="img-fluid theme-item"
          style={{ objectFit: "contain" }}
          loading="lazy"
          onClick={onClick}
        />
      </div>
      {/* d-block: text-truncate and w-100 are no-ops on an inline element */}
      <span
        className="d-block text-truncate text-center mt-1"
        style={{ fontSize: "11px", lineHeight: 1.3, textTransform: "capitalize" }}
        title={name}
      >
        {name}
      </span>
      <span
        className="d-block text-truncate text-center text-muted"
        style={{ fontSize: "10px", lineHeight: 1.3 }}
        title={meta}
      >
        {meta}
      </span>
    </div>
  );
});

export const MyProjectsAction = () => {
  const dispatch = useDispatch();
  // From redux, not the URL: a design opened by a share link has no `c_id` param.
  const cartOrderId = useSelector(getCartOrderId);
  const canvasSize = useSelector(getCanvasSize);
  const activeEditorType = useSelector(getActiveEditorType);
  const currentPageCount = useSelector(
    (state) => state.canvas.present.pages.length,
  );
  // canvas.editorType defaults to CALENDER (canvas.js) until the project loads,
  // so fetching before init would filter the list by the wrong editor type.
  const isProjectInitialized = useSelector(
    (state) => state.projectSetup.isInitialized,
  );

  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: () => { },
    showCancel: true,
    confirmText: "Yes",
  });

  const fetchProjects = useCallback(
    async (page, skip, limit) => {
      if (!cartOrderId) return { items: [], totalCount: 0 };
      const response = await apiPost(ENDPOINTS.getMyProjects, {
        cart_order_id: cartOrderId,
        editor_type: activeEditorType,
        skip: skip ?? (page - 1) * PROJECTS_PER_PAGE,
        limit: limit ?? PROJECTS_PER_PAGE,
      });
      return {
        items: response?.items || [],
        totalCount: response?.totalCount || 0,
      };
    },
    [cartOrderId, activeEditorType],
  );

  const {
    items: projects,
    loading,
    isFetchingMore,
    sentinelRef,
    scrollContainerRef,
  } = useInfiniteScroll({
    fetchFn: fetchProjects,
    itemsPerPage: PROJECTS_PER_PAGE,
    enabled: Boolean(cartOrderId && activeEditorType && isProjectInitialized),
    direction: "vertical",
  });

  const showError = (message) =>
    setConfirmDialog({
      show: true,
      title: "Design Unavailable",
      message,
      showCancel: false,
      confirmText: "OK",
      onConfirm: () => setConfirmDialog((prev) => ({ ...prev, show: false })),
    });

  const loadProject = async (project) => {
    // Reuses the existing project-details endpoint. Its response also carries the
    // OLD project's cartDetails/cart_order_id — read locally only, never dispatched
    // into projectSetup, or the next save would overwrite that other project.
    const response = await apiGet(
      ENDPOINTS.getProjectDetails + encodeURIComponent(project.cart_order_id),
    );
    const editorDetails = response?.items?.editorDetails;
    if (!editorDetails?.pages_c) {
      showError("This design could not be loaded. Please try another one.");
      return;
    }

    // Scale the saved design from its own size to the CURRENT canvas size —
    // same single-pass path the saved-project load and theme apply use.
    const layoutWidth =
      activeEditorType === EDITOR_TYPES.PHOTOBOOK
        ? canvasSize.width / 2
        : canvasSize.width;
    let pages = processProjectPages(
      editorDetails.pages_c,
      layoutWidth,
      canvasSize.height,
    );

    const maskIds = getMaskIds(pages);
    if (maskIds.length > 0) {
      const maskDetails = await getMasksByIds(maskIds);
      if (maskDetails?.length > 0) {
        pages = replaceMaskIdWithPath(pages, maskDetails);
      }
    }

    // Photos: the loaded design contributes its LAYOUT only — its own photos are
    // stripped and the customer's current photos are re-placed into the empty
    // slots afterwards. Read the pages via store.getState() rather than a
    // useSelector subscription: this component must not re-render on every canvas
    // edit, and this way the list is guaranteed fresh at click time.
    const imagesToCarryOver = collectPlacedImages(
      store.getState().canvas.present.pages,
    );
    pages = clearPlacedImages(pages);

    const carriedSettings = {};
    GEOMETRY_SETTING_KEYS.forEach((key) => {
      if (editorDetails.settings?.[key] !== undefined) {
        carriedSettings[key] = editorDetails.settings[key];
      }
    });
    if (Object.keys(carriedSettings).length > 0) {
      dispatch(setSettings(carriedSettings));
    }

    if (editorDetails.cal_settings) {
      // Drop the old start month/year so a reused calendar design starts fresh.
      dispatch(
        setCalendarSettings({
          ...editorDetails.cal_settings,
          startMonth: null,
          startYear: null,
        }),
      );
    }

    // Loading a project must not land in the undo stack at all. `canvas/applyTheme`
    // is already in store.jsx's ignoredCanvasActions, so the layout swap was never
    // recorded — which left the photo refill as the ONLY recorded entry, so one
    // Ctrl+Z emptied every box of the design just loaded. `setPageNumber` records
    // too (its payload is a plain number, so it cannot carry a `history: false`).
    //
    // Suppressed globally for this block rather than per-action, because
    // changeObjectsInAllPages is also dispatched by Auto-Create, which DOES want an
    // undo step — so the reducer and the shared ignore list must stay untouched.
    // All dispatches below are synchronous (no await), so nothing unrelated can slip
    // inside the window. The `finally` is essential: leaving the global flag on
    // would silently disable undo for the rest of the session.
    setSuppressLayoutHistory(true);
    try {
      dispatch(setCurrentObjectProperties(null));
      dispatch(applyTheme(pages));
      dispatch(deSelectSafeArea());
      dispatch(setPageNumber(0));

      // Re-place the customer's own photos into the new layout's empty slots, in
      // page order. option1 = fill empty boxes only, which is all of them now.
      // Must run AFTER applyTheme — the reducer works on state.pages.
      if (imagesToCarryOver.length > 0) {
        dispatch(
          changeObjectsInAllPages({
            images: imagesToCarryOver,
            option: "option1",
          }),
        );
      }
    } finally {
      setSuppressLayoutHistory(false);
    }

    dispatch(setIsActionActive(false));
  };

  const handleClick = (project) => {
    const pageMismatch =
      project.number_of_pages && project.number_of_pages !== currentPageCount;
    setConfirmDialog({
      show: true,
      title: "Use This Design",
      message: pageMismatch
        ? `This will use the design's layout and keep your current photos. It has ${project.number_of_pages} pages, but your current product has ${currentPageCount} - Check the page count before ordering. Continue?`
        : "This will replace your current layout and re-place your photos into it. Continue?",
      showCancel: true,
      confirmText: "Yes",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, show: false }));
        try {
          await loadProject(project);
        } catch (error) {
          console.error("Failed to load saved design:", error);
          showError("This design could not be loaded. Please try another one.");
        }
      },
    });
  };

  return (
    <>
      <div
        className="sticker-container sticker-container-mob"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          margin: "2px",
        }}
      >
        <DisplayBetween
          className="heading-action-mob"
          style={{ flexShrink: 0, borderBottom: "1px solid #f0f0f0" }}
        >
          <ActionTitle>My Projects</ActionTitle>
          <LiaTimesSolid
            onClick={() => dispatch(setIsActionActive(false))}
            className="cursor-pointer"
          />
        </DisplayBetween>
        <div
          ref={scrollContainerRef}
          className="scroll-container-mob"
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            minHeight: 0,
            padding: "3px",
            paddingBottom: "20px",
          }}
        >
          {loading && <PageLoader />}
          {!loading && projects.length === 0 && (
            <p className="text-muted text-center mt-4" style={{ fontSize: "13px" }}>
              You have no other saved designs yet.
            </p>
          )}
          <div className="mt-3">
            <div className="row">
              {projects.map((project) => (
                <ProjectGridItem
                  key={project._id}
                  project={project}
                  onClick={() => handleClick(project)}
                />
              ))}
            </div>
            <div ref={sentinelRef} style={{ height: "1px", width: "100%" }} />
            {isFetchingMore && <ScrollLoader />}
          </div>
        </div>
      </div>

      <ConfirmationDialog
        show={confirmDialog.show}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, show: false }))}
        showCancelButton={confirmDialog.showCancel}
        confirmText={confirmDialog.confirmText}
      />
    </>
  );
};
